/*! Card CTA Baseline Equalizer — v6 guard (clamp + broader roots + opt-out)
 *  Based on your v6 iOS-safe script; adds:
 *   - Clamp: never pushes CTA beyond card
 *   - Broader ROOTS to catch new sections/sliders
 *   - Opt-out hook: skip anything inside [data-no-cta-eq], or Best Sellers tabs
 */
(() => {
  const WRAPPER_SEL = '.card-wrapper.product-card-wrapper';
  const CTA_SEL = '.product-cta';
  const ROOTS = [
    '.product-grid', '.grid', '.slider', '[id^="Slider-"]',
    '.slider-component', '.slideshow', '.collection', '.collection-list',
    '.product-recommendations', '.featured-collection', '[class*="collection_tabs"]', '.collection-tabs'
  ];

  // Skip scopes
  const SKIP_SCOPE = [
    '[data-no-cta-eq]',
    '[class*="collection_tabs"]', '[class*="collection-tabs"]',
    '[id*="collection_tabs"]', '[id*="collection-tabs"]'
  ].join(', ');
  const skipScope = (el) => !!(el && el.closest && el.closest(SKIP_SCOPE));

  let scheduled = false;
  let lastWidth = window.innerWidth;

  const $ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function modalOpen(){ return document.body.classList.contains('overflow-hidden'); }

  function visible(el){
    if (!el) return false;
    if (el.closest('.quick-add-modal')) return false; // ignore QV modal
    const st = getComputedStyle(el);
    if (st.display === 'none' || st.visibility === 'hidden') return false;
    return el.offsetWidth > 0 && el.offsetHeight > 0;
  }

  function groupByRow(items){
    const BUCKET = 4;
    const rows = new Map();
    for (const el of items){
      const key = Math.floor(el.offsetTop / BUCKET) * BUCKET;
      if (!rows.has(key)) rows.set(key, []);
      rows.get(key).push(el);
    }
    return Array.from(rows.values());
  }

  function equalize(scope = document){
    scheduled = false;
    if (modalOpen()) return;

    let wrappers = [];
    const roots = ROOTS.map(s => $(s, scope)).flat();
    if (roots.length) roots.forEach(r => wrappers.push(...$(WRAPPER_SEL, r)));
    else wrappers = $(WRAPPER_SEL, scope);

    wrappers = wrappers.filter(visible).filter(w => !skipScope(w));
    if (!wrappers.length) return;

    // Reset previous padding
    for (const w of wrappers){
      const cta = w.querySelector(CTA_SEL);
      if (cta) cta.style.setProperty('--cta-extra', '0px');
    }

    const rows = groupByRow(wrappers);
    const updates = [];

    for (const row of rows){
      // natural distances
      const distances = row.map(w => {
        const cta = w.querySelector(CTA_SEL);
        if (!cta) return 0;
        return cta.offsetTop + cta.offsetHeight; // from wrapper top
      });
      const maxD = Math.max(...distances);

      row.forEach((w, i) => {
        const cta = w.querySelector(CTA_SEL);
        if (!cta) return;

        const target = Math.max(0, maxD - distances[i]);

        // ---- clamp: do not exceed remaining space inside the card
        const wrapperH = w.clientHeight || w.offsetHeight || 0;
        const ctaBottomNow = cta.offsetTop + cta.offsetHeight; // with current (reset) extra
        const remaining = Math.max(0, wrapperH - ctaBottomNow);
        const clamped = Math.max(0, Math.min(target, remaining));

        if (clamped > 0.5) updates.push([cta, clamped]);
      });
    }

    if (updates.length){
      requestAnimationFrame(() => {
        for (const [cta, extra] of updates){
          cta.style.setProperty('--cta-extra', extra + 'px');
        }
      });
    }
  }

  function schedule(scope = document){
    if (scheduled) return;
    if (modalOpen()){ setTimeout(() => schedule(scope), 120); return; }
    scheduled = true;
    requestAnimationFrame(() => equalize(scope));
    setTimeout(() => equalize(scope), 140);
    setTimeout(() => equalize(scope), 420);
  }

  function onResize(){
    const w = window.innerWidth;
    if (Math.abs(w - lastWidth) >= 1){
      lastWidth = w;
      schedule(document);
    }
  }

  function observe(){
    const mo = new MutationObserver(muts => {
      for (const m of muts){
        if (m.type === 'childList'){
          for (const n of m.addedNodes){
            if (n.nodeType !== 1) continue;
            if (n.matches?.(WRAPPER_SEL) || n.querySelector?.(WRAPPER_SEL)){ schedule(document); return; }
            // New sections/sliders
            if (ROOTS.some(sel => n.matches?.(sel) || n.querySelector?.(sel))) { schedule(document); return; }
          }
        }
        if (m.type === 'attributes'){
          if (ROOTS.some(sel => m.target.matches?.(sel))) { schedule(m.target); return; }
        }
      }
    });

    document.querySelectorAll('.shopify-section, ' + ROOTS.join(', ')).forEach(t => {
      mo.observe(t, { childList:true, subtree:true, attributes:true, attributeFilter:['class','style','hidden','aria-hidden'] });
    });

    window.addEventListener('resize', onResize, { passive:true });
    window.addEventListener('orientationchange', () => schedule(document), { passive:true });
    window.addEventListener('load', () => schedule(document));
    document.addEventListener('shopify:section:load', e => schedule(e.target || document));
    document.addEventListener('product-recommendations:loaded', () => schedule(document));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => schedule(document)).catch(() => {});
  }

  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', observe, { once:true });
  } else {
    observe();
  }

  window.equalizeCtas = () => schedule(document);
})();